import React, { Suspense, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { BrowserRouter as Router, Route } from 'react-router-dom'

import * as Sentry from '@sentry/react'
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { setOrgUsersById } from 'slices/orgUsersById'

import { ThemeProvider, ToastProvider } from '@driftt/dds'
import { DriftAnalytics } from '@driftt/dds.analytics'
import { access } from '@driftt/traffic-control'

import Skeleton from 'components/Skeleton'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'

import { getCurrentUser, getEnabledIntegrations, getOrgUsers } from '../../selectors'
import { ActivitiesPackageSubRoutes } from '../AccountActivity'

import '@driftt/react-dates/initialize'

import '@driftt/react-dates/lib/css/_datepicker.css'
import styles from '../AccountActivity/styles.module.css'
const getOrgUsersById = (users: any) =>
  users.reduce(
    (arr: any, user: any) => ({
      ...arr,
      [user.id]: user
    }),
    {}
  )

const App = () => {
  const orgUsers = useSelector(getOrgUsers)
  const currentUser = useSelector(getCurrentUser)
  const enabledIntegrations = useSelector(getEnabledIntegrations)
  const dispatch = useAppDispatch()
  const { loaded } = useAppSelector(({ orgUsersById }) => orgUsersById)
  const orgUsersById = getOrgUsersById(orgUsers)

  const hasProspector = access.isRbacUser()
    ? access.hasFeatures([{ featureName: 'prospector', accessLevel: 'READ' }])
    : access.hasGates(['prospector', 'has_account_activity'])
  const hasLiveView = access.isRbacUser()
    ? access.hasFeatures([{ featureName: 'prospector', accessLevel: 'READ' }])
    : access.hasGates('has_live_view')

  useEffect(() => {
    currentUser &&
      DriftAnalytics.initialize({
        user: currentUser,
      })
  }, [currentUser])
  

  useEffect(() => {
    if (!loaded && !isEmpty(orgUsersById)) {
      dispatch(setOrgUsersById(orgUsersById))
    }
  }, [orgUsersById, dispatch, loaded])

  if (!hasProspector && hasLiveView) window.location.replace('/live')
  if (!hasProspector && !hasLiveView) window.location.replace('/conversations')

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000*60*2,
      },
    },
  })

  return (
    <Sentry.ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <QueryClientProvider client={queryClient}>
            <Router basename="/">
              <div className={styles.app}>
                <Suspense fallback={<Skeleton />}>
                  <Route
                    path="/activity"
                    render={props => (
                      <ActivitiesPackageSubRoutes
                        {...props}
                        currentUser={currentUser}
                        enabledIntegrations={enabledIntegrations}
                      />
                    )}
                  />
                </Suspense>
              </div>
            </Router>
          </QueryClientProvider>
        </ToastProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  )
}

export default Sentry.withProfiler(App)
